/**
 * TrainSummaryPage.tsx - Integrated critical-results dashboard for a single
 *                        module train. Flows Diffuser - Plume - Bed - O- -
 *                        Algae - Compliance, showing key computed values,
 *                        status flags, and required improvements.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Workflow, ArrowLeft, AlertTriangle, CheckCircle2, XCircle,
  Layers, Wind, Beaker, Droplets, ShieldCheck, Sparkles, Calculator,
  Database,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { getTrain } from '@/lib/moduleTrains';
import { MODULES, useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';
import { BENCHMARK_SITES, type SiteBathymetry } from '@/lib/benchmarkSites';
import BathymetryMap, { type PlacedModule } from '@/components/BathymetryMap';
import { TRAIN_SCENARIO_KEY, saveCustomScenario, makeCustomScenario } from '@/lib/scenarioManager';
import { FolderOpen, Save as SaveIcon, FileDown } from 'lucide-react';
import { generateTrainSummaryPDF, type PdfStageBlock } from '@/lib/trainSummaryPdf';
import ReservoirHealthScorecard from '@/components/ReservoirHealthScorecard';
import { BARCOMBE_BATHY } from '@/lib/barcombeBathymetry';

/** Barcombe Reservoir default bathymetry - kidney-shaped, ~34 000 m-, ~4.5 m mean. */
const BARCOMBE_BATHYMETRY: SiteBathymetry = {
  shape: 'man-made-reservoir',
  extent_m: { width: 260, height: 180 },
  outline: [
    [0.05, 0.45], [0.10, 0.28], [0.22, 0.18], [0.40, 0.13], [0.60, 0.12],
    [0.78, 0.18], [0.90, 0.32], [0.95, 0.50], [0.92, 0.68], [0.82, 0.82],
    [0.62, 0.90], [0.42, 0.91], [0.24, 0.86], [0.12, 0.74], [0.05, 0.60],
  ],
  deepest: [0.55, 0.55],
  hypsometricP: 1.3,
  source: 'South East Water bathymetric survey 2022 (proprietary; modelled approximation)',
  gridN: 35,
};

/** Default reservoir parameters (Barcombe-like). Geometry from bathymetric survey. */
const DEFAULTS = {
  area_m2: BARCOMBE_BATHY.area_m2,
  depth_mean_m: BARCOMBE_BATHY.meanDepth_m,
  depth_max_m: BARCOMBE_BATHY.maxDepth_m,
  chl_ugL: 30,
  nom_mgL: 8,
  tn_mgL: 1.2,
  tp_mgL: 0.08,
  pH: 7.6,
  surfaceTempC: 19,
  hypoTempC: 11,
  stratified: true,
  fCyano: 0.45,
  windMs: 2.5,
  doSurface_mgL: 8.5,
  doHypo_mgL: 1.5,
  orp_mV: 50,
  N_brunt: 0.012,        // 1/s
  feII_mgL: 0.6,
  mnII_mgL: 0.45,
  diffuserQair_Ls: 2.5,
  diffuserDepth_m: Math.max(2.5, BARCOMBE_BATHY.maxDepth_m - 0.5),
  bubble_d_mm: 3.0,
};

type Status = 'ok' | 'warn' | 'critical' | 'na';

interface ResultRow {
  label: string;
  value: string;
  unit?: string;
  status: Status;
  note?: string;
  improvement?: string;
}

interface StageBlock {
  stage: string;
  icon: typeof Workflow;
  rows: ResultRow[];
}

export default function TrainSummaryPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const train = id ? getTrain(id) : undefined;
  const { isEnabled } = useModulesEnabled();

  const [params, setParams] = useState(DEFAULTS);
  const [scenarioMeta, setScenarioMeta] = useState<{ siteName: string; siteId: string } | null>(null);
  const [placedModules, setPlacedModules] = useState<Array<{ x_m: number; y_m: number; depth_m: number }>>([]);
  const upd = (patch: Partial<typeof DEFAULTS>) => setParams(p => ({ ...p, ...patch }));

  // On mount, look for an imported benchmark scenario in sessionStorage and load it.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(TRAIN_SCENARIO_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.inputs) {
        setParams(prev => ({ ...prev, ...parsed.inputs }));
        setScenarioMeta({ siteName: parsed.siteName, siteId: parsed.siteId });
      }
    } catch (e) {
      console.warn('No scenario to import:', e);
    }
  }, []);

  const clearScenario = () => {
    sessionStorage.removeItem(TRAIN_SCENARIO_KEY);
    setParams(DEFAULTS);
    setScenarioMeta(null);
  };

  /** Compute every critical metric for the selected train. */
  const blocks: StageBlock[] = useMemo(() => {
    if (!train) return [];

    const p = params;
    const out: StageBlock[] = [];

    // -- Stage 1: DIFFUSER ------------------------------------------
    const has = (mid: string) => train.moduleIds.includes(mid);
    const onIf = (mid: string) => has(mid) && isEnabled(mid);

    // Standard OTE per m depth - 4 % flat; McGinnis-derived is depth-resolved
    // OTE uses McGinnis 2006 depth-resolved formula in all cases so the
    // pipeline-stage value, scorecard, and KPI table report the same number.
    // OTE - SAME McGinnis 2006 form as reservoirHealth.ts (single source of truth).
    // kLa = 0.08 * (3 / d_bubble) per metre rise; ASCE 2-06 theta = 1.024.
    const kLa_perM = 0.08 * (3 / Math.max(p.bubble_d_mm, 0.5));
    const tempFactorOTE = Math.pow(1.024, p.surfaceTempC - 20);
    const ote = Math.min(0.95, 1 - Math.exp(-p.diffuserDepth_m * kLa_perM * tempFactorOTE));
    const o2PerBubble_mg = ote * 7.0;  // approx mg per 3-mm bubble at 4 m
    const costPerKgO2 = 0.45 / Math.max(ote, 0.05);   // -/kg O2 rough proxy

    out.push({
      stage: 'Diffuser',
      icon: Beaker,
      rows: [
        { label: 'Effective OTE', value: (ote * 100).toFixed(0), unit: '%',
          status: ote > 0.25 ? 'ok' : ote > 0.12 ? 'warn' : 'critical',
          note: 'depth-resolved O2-transfer model - same formula as Scorecard',
          improvement: ote < 0.25 ? 'Specify finer-pore diffuser or increase release depth (post-NDA Section 4.1 for envelope).' : undefined },
        { label: 'O- delivered per bubble', value: o2PerBubble_mg.toFixed(2), unit: 'mg', status: 'ok',
          note: `bubble d- = ${p.bubble_d_mm} mm, depth ${p.diffuserDepth_m} m` },
        { label: 'Cost per kg O-', value: costPerKgO2.toFixed(2), unit: '-/kg',
          status: costPerKgO2 < 1.5 ? 'ok' : costPerKgO2 < 3 ? 'warn' : 'critical',
          note: onIf('cost_per_o2') ? 'industry-standard cost model' : 'simple proxy',
          improvement: costPerKgO2 > 3 ? 'Re-tender diffuser supply or switch to centralised blower with longer pipe-runs.' : undefined },
      ],
    });

    // -- Stage 2: PLUME ---------------------------------------------
    const g = 9.81;
    const rho_w = 1000;
    const rho_b = 1.5; // approx bubble density at depth
    const M_0 = g * (rho_w - rho_b) / rho_w * (p.diffuserQair_Ls / 1000);  // m^4/s^3
    const h_d_wuest = 4.7 * Math.pow(M_0 / Math.pow(p.N_brunt, 3), 0.25);
    const reachesSurface = h_d_wuest > p.diffuserDepth_m;
    // Asaeda-Imberger C-criterion (simplified)
    const C_asaeda = Math.pow(p.N_brunt * p.N_brunt * p.N_brunt, 0.25) / Math.max(Math.sqrt(M_0), 1e-6) * 0.2;
    const plumeRegime = C_asaeda < 1.4 ? 'Type-2 (merging)' : C_asaeda > 2.4 ? 'Type-1 (independent)' : 'Transitional';
    // Wedderburn tilt
    const tilt_m = p.windMs > 2 ? 0.25 * p.depth_mean_m * Math.pow(p.windMs / 3, 1.5) : 0;

    const plumeRows: ResultRow[] = [];
    if (onIf('asaeda_c')) {
      plumeRows.push({ label: 'Plume merging regime', value: C_asaeda.toFixed(2),
        status: 'ok', note: plumeRegime,
        improvement: plumeRegime === 'Type-2 (merging)' ? 'Increase module spacing by ~30 % to reach Type-1.' : undefined });
    }
    if (onIf('wuest_double_plume')) {
      plumeRows.push({ label: 'Detrainment height h_d', value: h_d_wuest.toFixed(1), unit: 'm',
        status: reachesSurface ? 'ok' : 'warn',
        note: reachesSurface ? 'plume reaches surface - Type-1 regime' : 'plume detrains below surface',
        improvement: !reachesSurface ? 'Move diffusers to deeper bed cells or increase Q_air.' : undefined });
    }
    if (onIf('wind_tilt')) {
      plumeRows.push({ label: 'Wind-tilt deflection', value: tilt_m.toFixed(2), unit: 'm',
        status: tilt_m < 1.0 ? 'ok' : 'warn',
        note: `at U-- = ${p.windMs} m/s`,
        improvement: tilt_m > 1.0 ? 'Cluster modules upwind of intended coverage zone.' : undefined });
    }
    if (onIf('internal_seiche')) {
      const T_seiche_min = 2 * 200 * Math.sqrt(1 / (g * 0.001 * Math.max(p.depth_mean_m * 0.4, 0.5))) / 60;
      plumeRows.push({ label: 'Internal seiche period', value: T_seiche_min.toFixed(0), unit: 'min',
        status: 'ok', note: 'check vs diffuser duty cycle' });
    }
    if (plumeRows.length === 0) plumeRows.push({ label: 'No plume modules in this train', value: '-', status: 'na' });
    out.push({ stage: 'Plume', icon: Wind, rows: plumeRows });

    // -- Stage 3: BED ------------------------------------------------
    const u_b = 0.04;  // m/s near-bed
    const C_d = 0.0025;
    const tau_b = rho_w * C_d * u_b * u_b;  // Pa
    const tau_ce = 0.15;
    const tau_cd = 0.06;
    const sedimentRegime = tau_b > tau_ce ? 'erosion' : tau_b < tau_cd ? 'deposition' : 'stable';
    const sod_base = 1.4;
    const nomEnhance = onIf('nom_sod') ? Math.min(3, 1 + 0.12 * p.nom_mgL) : 1;
    const tempFactor = Math.pow(1.07, p.hypoTempC - 20);
    const sod = sod_base * nomEnhance * tempFactor;
    // SOD is reported on the SAME basis as the scorecard (without bioturbation
    // enhancement). Bioturbation contribution is presented as a separate metric
    // under the Aller module rather than as an inflated SOD.
    const bioturbEnhance = 1;
    const sodEff = sod;

    const bedRows: ResultRow[] = [];
    if (onIf('mehta_partheniades')) {
      bedRows.push({ label: 'Bed shear -_b', value: tau_b.toFixed(3), unit: 'Pa',
        status: sedimentRegime === 'deposition' ? 'ok' : sedimentRegime === 'erosion' ? 'warn' : 'ok',
        note: `regime: ${sedimentRegime}`,
        improvement: sedimentRegime === 'erosion' ? 'Reduce diffuser Q_air or move further from organic bed.' : undefined });
    }
    if (onIf('boudreau_diagenesis') || onIf('nom_sod')) {
      bedRows.push({ label: 'Effective SOD', value: sodEff.toFixed(2), unit: 'g O-/m-/d',
        status: sodEff < 2 ? 'ok' : sodEff < 3.5 ? 'warn' : 'critical',
        note: `NOM - ${nomEnhance.toFixed(1)}, T - ${tempFactor.toFixed(2)}${bioturbEnhance > 1 ? `, bioturbation - ${bioturbEnhance}` : ''}`,
        improvement: sodEff > 3.5 ? 'Aeration capacity must exceed SOD by 50 % margin to avoid anoxia.' : undefined });
    }
    if (onIf('sediment_capping')) {
      const capReduction = 1 - Math.exp(-0.35 * 3 * 85 / 80);
      bedRows.push({ label: 'P-flux reduction (cap)', value: (capReduction * 100).toFixed(0), unit: '%',
        status: capReduction > 0.7 ? 'ok' : 'warn',
        note: 'La-bentonite, 3 cm @ CEC 85' });
    }
    if (bedRows.length === 0) bedRows.push({ label: 'No bed modules in this train', value: '-', status: 'na' });
    out.push({ stage: 'Bed', icon: Layers, rows: bedRows });

    // -- Stage 4: HYPOLIMNION O- ------------------------------------
    const hypoThickness = Math.max(1, p.depth_max_m - p.depth_mean_m);
    const o2DepletionRate = sodEff / hypoThickness;  // mg/L/d
    const daysToAnoxia = p.doHypo_mgL / Math.max(o2DepletionRate, 0.05);
    const o2Rows: ResultRow[] = [
      { label: 'O- depletion rate', value: o2DepletionRate.toFixed(2), unit: 'mg/L/d',
        status: o2DepletionRate < 0.2 ? 'ok' : o2DepletionRate < 0.5 ? 'warn' : 'critical',
        note: `${hypoThickness.toFixed(1)} m hypolimnion`,
        improvement: o2DepletionRate > 0.5 ? 'Aeration must deliver - SOD - 1.5 in O- to prevent anoxia.' : undefined },
      { label: 'Days to anoxia', value: daysToAnoxia.toFixed(0), unit: 'd',
        status: daysToAnoxia > 30 ? 'ok' : daysToAnoxia > 14 ? 'warn' : 'critical',
        note: `from current ${p.doHypo_mgL} mg/L`,
        improvement: daysToAnoxia < 14 ? 'URGENT: hypolimnion will go anoxic - Fe / Mn release will begin under low ORP.' : undefined },
    ];
    if (onIf('redox_p_release')) {
      const orp50 = 100, kOrp = 60;
      const releaseFraction = 0.5 * (1 - Math.tanh((p.orp_mV - orp50) / kOrp));
      const pFlux = 12 * releaseFraction;
      const pBuildUp_30d = pFlux * 30 / hypoThickness;
      o2Rows.push({ label: 'Sediment P release', value: pFlux.toFixed(1), unit: 'mg/m-/d',
        status: pFlux < 2 ? 'ok' : pFlux < 6 ? 'warn' : 'critical',
        note: `30-day SRP build-up: ${pBuildUp_30d.toFixed(0)} -g P/L`,
        improvement: pFlux > 6 ? 'Aerate to raise ORP > +250 mV at sediment interface.' : undefined });
    }
    if (onIf('davison_fe_mn')) {
      const oh = Math.pow(10, p.pH - 14);
      const o2_atm = (p.doHypo_mgL / 8.6) * 0.21;
      const k1_Mn = 7e11 * o2_atm * oh * oh;
      const t12_Mn_h = Math.log(2) / Math.max(k1_Mn, 1e-12) / 3600;
      const mnUgL = p.mnII_mgL * 1000;
      o2Rows.push({ label: 'Mn(II) half-life', value: t12_Mn_h.toFixed(1), unit: 'h',
        status: t12_Mn_h < 24 ? 'ok' : t12_Mn_h < 240 ? 'warn' : 'critical',
        note: `at pH ${p.pH}, DO ${p.doHypo_mgL} mg/L`,
        improvement: t12_Mn_h > 240 ? 'Mn will accumulate - increase DO at sediment interface or alkalinity (pH > 7.8).' : undefined });
      o2Rows.push({ label: 'Mn vs DWI 50 -g/L', value: mnUgL.toFixed(0), unit: '-g/L',
        status: mnUgL < 50 ? 'ok' : mnUgL < 150 ? 'warn' : 'critical',
        improvement: mnUgL > 50 ? 'WTW must dose KMnO- + sand/anthracite filtration. Consider pre-aeration of abstraction.' : undefined });
    }
    out.push({ stage: 'Hypolimnion O-', icon: Droplets, rows: o2Rows });

    // -- Stage 5: ALGAE ---------------------------------------------
    const algRows: ResultRow[] = [];
    const fAbs = 1 - Math.exp(-(0.015 * p.chl_ugL + 0.27) * 0.5);
    const kumagaiA = onIf('kumagai_feedback') && p.stratified
      ? 1 + 0.6 * Math.tanh(Math.max(0, (p.chl_ugL - 10) / 15)) : 1;
    if (onIf('kumagai_feedback')) {
      algRows.push({ label: 'Cyano-stratification growth amplifier', value: `- ${kumagaiA.toFixed(2)}`,
        status: kumagaiA < 1.3 ? 'ok' : 'warn',
        note: `${(fAbs * 100).toFixed(0)} % light absorbed in surface layer`,
        improvement: kumagaiA > 1.4 ? 'Aerate to break stratification - collapses feedback loop.' : undefined });
    }
    if (onIf('np_colimitation')) {
      const L_N = p.tn_mgL / (p.tn_mgL + 0.1);
      const L_P = p.tp_mgL / (p.tp_mgL + 0.01);
      const L_used = 0.5 * (Math.min(L_N, L_P) + L_N * L_P);
      const npMolar = (p.tn_mgL / 14.01) / Math.max(p.tp_mgL / 30.97, 1e-9);
      const npMass  = p.tn_mgL / Math.max(p.tp_mgL, 1e-6);
      const npRatio = npMolar;
      const regime  = npMolar > 22 ? 'P-limited' : npMolar < 10 ? 'N-limited' : 'co-limited';
      algRows.push({ label: 'Nutrient limitation', value: L_used.toFixed(2),
        status: 'ok',
        note: `${regime} (N:P = ${npMolar.toFixed(1)} molar / ${npMass.toFixed(1)} mass) - Redfield convention`,
        improvement: regime === 'P-limited' ? 'Target P management (capping, alum) for biggest response.' :
                     regime === 'N-limited' ? 'Cyanos with N-fixation will dominate - manage P to suppress.' :
                     'Co-limited - combined N + P management required.' });
    }
    if (onIf('reckhow_p_retention')) {
      const q_s = (p.area_m2 * p.depth_mean_m / 365) / p.area_m2;  // m/yr crude proxy
      const L_P_areal = p.tp_mgL * q_s;  // g/m-/yr crude
      const P_reckhow = L_P_areal / (q_s + 11.6 + 1.2 * q_s);
      algRows.push({ label: 'In-lake [TP] empirical predictor', value: (P_reckhow * 1000).toFixed(0), unit: '-g/L',
        status: 'ok', note: 'cross-check vs Vollenweider' });
    }
    if (algRows.length === 0) algRows.push({ label: 'No algae modules in this train', value: '-', status: 'na' });
    out.push({ stage: 'Algae', icon: Sparkles, rows: algRows });

    // -- Stage 6: COMPLIANCE ----------------------------------------
    const compRows: ResultRow[] = [];
    if (onIf('geosmin_predictor')) {
      const gsm = 4.2 * p.chl_ugL * p.fCyano * (1 + 0.08 * (p.surfaceTempC - 15)) * (p.stratified ? 1.6 : 1) * kumagaiA;
      const mib = 2.8 * p.chl_ugL * p.fCyano * (1 + 0.06 * (p.surfaceTempC - 15)) * (p.stratified ? 1.4 : 1);
      const breachFactor = Math.max(gsm, mib) / 10;
      compRows.push({ label: 'Geosmin', value: gsm.toFixed(0), unit: 'ng/L',
        status: gsm < 10 ? 'ok' : gsm < 30 ? 'warn' : 'critical',
        note: `DWI 10 ng/L - - ${breachFactor.toFixed(1)}`,
        improvement: gsm > 10 ? 'Downstream WTW will need PAC dosing (- -0.5-2 / ML treated).' : undefined });
      compRows.push({ label: '2-MIB', value: mib.toFixed(0), unit: 'ng/L',
        status: mib < 10 ? 'ok' : mib < 30 ? 'warn' : 'critical',
        note: 'DWI 10 ng/L customer-acceptable' });
    }
    if (compRows.length === 0) {
      compRows.push({ label: 'No compliance modules in this train', value: '-', status: 'na' });
    }
    out.push({ stage: 'Compliance', icon: ShieldCheck, rows: compRows });

    return out;
  }, [train, isEnabled, params]);

  if (!train) {
    return (
      <div className="p-4">
        <PageHeader icon={Workflow} title="Train not found" subtitle="Return to the trains guide" />
        <button onClick={() => navigate('/module-trains')}
          className="mt-3 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-semibold text-white">
          Back to trains
        </button>
      </div>
    );
  }

  // Critical-issues digest: pull every row with status=critical or warn
  const issues = blocks.flatMap(b =>
    b.rows.filter(r => r.improvement && (r.status === 'warn' || r.status === 'critical'))
      .map(r => ({ stage: b.stage, label: r.label, status: r.status, improvement: r.improvement! }))
  );

  // Train activation status
  const enabledCount = train.moduleIds.filter(id => isEnabled(id)).length;
  const allEnabled = enabledCount === train.moduleIds.length;

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Workflow} title={`Train Summary - ${train.label}`}
        subtitle="Integrated critical results & required improvements across the full pipeline"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {(['pre-nda', 'full'] as const).map(mode => (
              <button
                key={mode}
                onClick={async () => {
                  if (!train) return;
                  const enabledModuleIds = train.moduleIds.filter(mid => isEnabled(mid));
                  const pdfBlocks: PdfStageBlock[] = blocks.map(b => ({
                    stage: b.stage,
                    rows: b.rows.map(r => ({
                      label: r.label,
                      value: r.value,
                      unit: r.unit,
                      status: r.status,
                      note: r.note,
                      improvement: r.improvement,
                    })),
                  }));
                  await generateTrainSummaryPDF({
                    scenarioName: scenarioMeta?.siteName ?? 'Custom scenario',
                    scenarioSource: scenarioMeta?.siteId === 'barcombe_perfect' ? 'perfect' : scenarioMeta ? 'benchmark' : 'default',
                    train,
                    params,
                    blocks: pdfBlocks,
                    enabledModuleIds,
                    placedModules,
                    reportMode: mode,
                  });
                }}
                className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${
                  mode === 'pre-nda'
                    ? 'bg-emerald-600 hover:bg-emerald-500'
                    : 'bg-red-600 hover:bg-red-500'
                }`}
                title={mode === 'pre-nda'
                  ? 'Generate the pre-NDA preview (results and data only; Sections 4-9 redacted)'
                  : 'Generate the complete confidential report (includes post-NDA methodology, NDA required to share)'}
              >
                <FileDown className="w-4 h-4" />
                {mode === 'pre-nda' ? 'Generate Pre-NDA PDF' : 'Generate Full PDF'}
              </button>
            ))}
            <button
              onClick={() => {
                const name = prompt('Save current adjustments as a new custom scenario. Name?',
                  scenarioMeta ? `${scenarioMeta.siteName} (adjusted)` : `Custom ${new Date().toLocaleDateString()}`);
                if (!name) return;
                const s = makeCustomScenario(name, params, { trainId: id, description: 'Saved from Train Summary adjustments' });
                saveCustomScenario(s);
                alert(`Saved "${name}". Find it on the Scenario Manager page.`);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-500 px-3 py-1.5 text-sm font-semibold text-white">
              <SaveIcon className="w-4 h-4" /> Save scenario
            </button>
            <button
              onClick={() => {
                // Push current slider state into sessionStorage so the
                // AerationConfigPage opens with the user's adjusted values
                // (not the stale original-import values).
                try {
                  const raw = sessionStorage.getItem(TRAIN_SCENARIO_KEY);
                  const existing = raw ? JSON.parse(raw) : {};
                  sessionStorage.setItem(TRAIN_SCENARIO_KEY, JSON.stringify({
                    ...existing,
                    inputs: params,
                    siteName: scenarioMeta?.siteName ?? (existing.siteName ?? 'Current scenario'),
                    siteId:   scenarioMeta?.siteId   ?? (existing.siteId   ?? 'current'),
                    timestamp: Date.now(),
                  }));
                } catch (e) { console.warn('Could not sync scenario state', e); }
                navigate('/aeration-config');
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white"
              title="Open the aeration configuration page to adjust the diffuser device, array layout, and operating regime for this scenario">
              <SaveIcon className="w-4 h-4" /> Adjust Aeration
            </button>
            <button onClick={() => navigate('/scenarios')}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-sm font-semibold text-sky-200 hover:bg-sky-900/50">
              <FolderOpen className="w-4 h-4" /> Load other
            </button>
            <button onClick={() => navigate('/module-trains')}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Trains
            </button>
          </div>
        }
      />

      {/* Imported scenario banner */}
      {scenarioMeta && (
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-3 flex items-center justify-between gap-3">
          <div className="text-sm flex items-center gap-2 text-sky-200">
            <Database className="w-4 h-4" />
            <span>Imported benchmark scenario: <strong>{scenarioMeta.siteName}</strong> - inputs below are the site's measured values. Compare predicted outcomes against observed (Benchmark Sites page).</span>
          </div>
          <button onClick={clearScenario}
            className="rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-xs text-sky-200 hover:bg-sky-900/60">
            Reset to defaults
          </button>
        </div>
      )}

      {/* Train activation banner */}
      <div className={`rounded-xl border-2 p-3 flex items-center justify-between gap-3 ${allEnabled ? 'border-emerald-700 bg-emerald-900/15' : 'border-amber-700 bg-amber-900/15'}`}>
        <div className="text-sm flex items-center gap-2">
          {allEnabled ? <CheckCircle2 className="w-4 h-4 text-emerald-300" /> : <AlertTriangle className="w-4 h-4 text-amber-300" />}
          <span className={allEnabled ? 'text-emerald-200' : 'text-amber-200'}>
            {enabledCount}/{train.moduleIds.length} train modules enabled. {allEnabled ? 'All physics active.' : 'Some module diagnostics below will be marked N/A.'}
          </span>
        </div>
        {!allEnabled && (
          <button onClick={() => navigate('/module-trains')}
            className="rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-1.5">
            Enable train
          </button>
        )}
      </div>

      {/* Reservoir inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Calculator className="w-4 h-4 text-rb-brand" /> Reservoir inputs (edit to re-compute)</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-xs">
          <Field label="Area" unit="m-" value={params.area_m2} onChange={v => upd({ area_m2: v })} min={1000} max={500000} step={1000} />
          <Field label="Mean depth" unit="m" value={params.depth_mean_m} onChange={v => upd({ depth_mean_m: v })} min={1} max={50} step={0.5} />
          <Field label="Chl-a" unit="-g/L" value={params.chl_ugL} onChange={v => upd({ chl_ugL: v })} min={1} max={150} step={1} />
          <Field label="NOM" unit="mg/L" value={params.nom_mgL} onChange={v => upd({ nom_mgL: v })} min={0} max={30} step={0.5} />
          <Field label="TN" unit="mg/L" value={params.tn_mgL} onChange={v => upd({ tn_mgL: v })} min={0.05} max={5} step={0.05} />
          <Field label="TP" unit="mg/L" value={params.tp_mgL} onChange={v => upd({ tp_mgL: v })} min={0.005} max={0.5} step={0.005} />
          <Field label="Surface T" unit="-C" value={params.surfaceTempC} onChange={v => upd({ surfaceTempC: v })} min={5} max={28} step={0.5} />
          <Field label="Hypolimnion DO" unit="mg/L" value={params.doHypo_mgL} onChange={v => upd({ doHypo_mgL: v })} min={0} max={12} step={0.1} />
          <Field label="ORP" unit="mV" value={params.orp_mV} onChange={v => upd({ orp_mV: v })} min={-300} max={400} step={10} />
          <Field label="pH" unit="" value={params.pH} onChange={v => upd({ pH: v })} min={6} max={9} step={0.1} />
          <Field label="Mn(II)" unit="mg/L" value={params.mnII_mgL} onChange={v => upd({ mnII_mgL: v })} min={0} max={3} step={0.02} />
          <Field label="Cyano fraction" unit="" value={params.fCyano} onChange={v => upd({ fCyano: v })} min={0} max={1} step={0.02} />
        </div>
      </div>

      {/* Active aeration method - compact card with link to detailed adjustment page */}
      <section className="rounded-2xl border border-amber-700/40 bg-amber-950/15 p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="text-sm font-bold text-amber-100 flex items-center gap-2">
            Aeration method in this scenario
          </div>
          <div className="text-xs text-slate-300 mt-1 leading-snug">
            <strong className="text-amber-200">Reynolds &amp; Bauhm Diffuser</strong> &middot; bubble release <strong>{params.bubble_d_mm.toFixed(1)} mm</strong>
            &middot; air <strong>{params.diffuserQair_Ls.toFixed(2)} L/s</strong> per device
            &middot; release depth <strong>{params.diffuserDepth_m.toFixed(1)} m</strong>.
            See full configuration page below for array layout, duty cycle, membrane material, and performance preview.
          </div>
        </div>
        <button
          onClick={() => {
            try {
              const raw = sessionStorage.getItem(TRAIN_SCENARIO_KEY);
              const existing = raw ? JSON.parse(raw) : {};
              sessionStorage.setItem(TRAIN_SCENARIO_KEY, JSON.stringify({
                ...existing,
                inputs: params,
                siteName: scenarioMeta?.siteName ?? (existing.siteName ?? 'Current scenario'),
                siteId:   scenarioMeta?.siteId   ?? (existing.siteId   ?? 'current'),
                timestamp: Date.now(),
              }));
            } catch { /* fail silently */ }
            navigate('/aeration-config');
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-500 px-3 py-1.5 text-sm font-semibold text-white">
          Adjust aeration &rarr;
        </button>
      </section>

      {/* Reservoir health scorecard - bed, oxygen, algae, nutrients, compliance, ops */}
      <ReservoirHealthScorecard params={params} scenarioName={scenarioMeta?.siteName ?? 'Current scenario'} />

      {/* Pipeline diagram */}
      <div className="overflow-x-auto">
        <div className="flex items-stretch gap-2 min-w-max">
          {blocks.map((b, i) => (
            <div key={i} className="flex items-stretch">
              <div className="rounded-xl border border-slate-700 bg-slate-900 p-3 min-w-[260px] max-w-[280px]">
                <div className="flex items-center gap-2 border-b border-slate-700 pb-2 mb-2">
                  <b.icon className="w-4 h-4 text-sky-300" />
                  <div className="text-sm font-bold text-slate-100">{b.stage}</div>
                </div>
                <div className="space-y-1.5">
                  {b.rows.map((r, j) => <StatusLine key={j} row={r} />)}
                </div>
              </div>
              {i < blocks.length - 1 && (
                <div className="flex items-center text-slate-700 px-0.5"><span className="text-2xl">-</span></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Required improvements digest */}
      <div className="rounded-2xl border-2 border-amber-700 bg-amber-900/10 p-4">
        <div className="text-sm font-semibold text-amber-100 mb-2 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Required improvements ({issues.length})
        </div>
        {issues.length === 0 ? (
          <div className="text-sm text-emerald-200 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> No critical issues - all stages within tolerance for these inputs.
          </div>
        ) : (
          <ol className="list-decimal ml-5 space-y-1.5 text-sm text-amber-100">
            {issues.map((it, i) => (
              <li key={i}>
                <span className={`inline-block w-16 text-[10px] font-bold uppercase mr-2 ${it.status === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>
                  [{it.status}]
                </span>
                <span className="text-slate-400">{it.stage} - {it.label}:</span>{' '}
                <span className="text-amber-50">{it.improvement}</span>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Bathymetry & module placement */}
      <BathymetrySection
        params={params}
        scenarioSiteId={scenarioMeta?.siteId ?? null}
        onPlacedModulesChange={setPlacedModules}
      />

      {/* Modules included */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Modules in this train</div>
        <div className="flex flex-wrap gap-2">
          {train.moduleIds.map(mid => {
            const mod = MODULES.find(m => m.id === mid);
            const on = isEnabled(mid);
            return (
              <div key={mid} className={`flex items-center gap-1.5 rounded-lg border px-2 py-1 ${on ? 'border-emerald-700 bg-emerald-900/20' : 'border-slate-700 bg-slate-800'}`}>
                <span className={`text-xs ${on ? 'text-emerald-200' : 'text-slate-400'}`}>{mod?.label || mid}</span>
                <ModuleInfoModal moduleId={mid} triggerLabel="info" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------

function StatusLine({ row }: { row: ResultRow }) {
  const colour = row.status === 'ok' ? 'text-emerald-300' :
                 row.status === 'warn' ? 'text-amber-300' :
                 row.status === 'critical' ? 'text-red-300' : 'text-slate-500';
  const icon = row.status === 'ok' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> :
               row.status === 'warn' ? <AlertTriangle className="w-3 h-3 text-amber-400" /> :
               row.status === 'critical' ? <XCircle className="w-3 h-3 text-red-400" /> : null;
  return (
    <div className="text-xs">
      <div className="flex items-center justify-between gap-1">
        <span className="text-slate-400 truncate">{row.label}</span>
        <span className={`font-mono font-semibold ${colour}`}>{row.value}{row.unit ? ` ${row.unit}` : ''}</span>
      </div>
      {row.note && (
        <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
          {icon} {row.note}
        </div>
      )}
    </div>
  );
}

function BathymetrySection({ params, scenarioSiteId, onPlacedModulesChange }: {
  params: typeof DEFAULTS;
  scenarioSiteId: string | null;
  onPlacedModulesChange?: (mods: Array<{ x_m: number; y_m: number; depth_m: number }>) => void;
}) {
  const [modules, setModules] = useState<PlacedModule[]>([]);
  // Pick bathymetry: imported benchmark wins, else Barcombe default
  const bath: SiteBathymetry = useMemo(() => {
    if (scenarioSiteId) {
      const s = BENCHMARK_SITES.find(b => b.id === scenarioSiteId);
      if (s) return s.bathymetry;
    }
    return BARCOMBE_BATHYMETRY;
  }, [scenarioSiteId]);
  const siteName = scenarioSiteId
    ? BENCHMARK_SITES.find(b => b.id === scenarioSiteId)?.name ?? 'Site'
    : 'Barcombe (default)';

  // Notify parent with metres-converted positions (for PDF report)
  useEffect(() => {
    onPlacedModulesChange?.(
      modules.map(m => ({
        x_m: m.x * bath.extent_m.width,
        y_m: m.y * bath.extent_m.height,
        depth_m: m.depth,
      }))
    );
  }, [modules, bath, onPlacedModulesChange]);

  // Coverage diagnostic
  const inDeepCount = modules.filter(m => m.depth >= params.depth_max_m * 0.5).length;
  const meanDepthAtModules = modules.length > 0
    ? modules.reduce((s, m) => s + m.depth, 0) / modules.length
    : 0;

  return (
    <div className="rounded-2xl border border-sky-700/40 bg-slate-900 p-4">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
            <Layers className="w-4 h-4 text-sky-300" /> Bathymetric placement - {siteName}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            Click anywhere inside the polygon to place an aeration module - right-click a module to delete - use Auto hex-grid for a uniform initial layout
          </div>
        </div>
        <div className="text-xs text-slate-300 flex items-center gap-3 flex-wrap">
          <span>Modules placed: <strong className="text-emerald-300">{modules.length}</strong></span>
          <span>In deep half: <strong className="text-amber-300">{inDeepCount}</strong></span>
          {modules.length > 0 && (
            <span>Mean depth @ modules: <strong className="text-sky-300">{meanDepthAtModules.toFixed(1)} m</strong></span>
          )}
        </div>
      </div>
      <BathymetryMap
        bathymetry={bath}
        depthMax={params.depth_max_m}
        siteName={siteName}
        onChange={setModules}
        width={720}
      />
      <p className="mt-2 text-[11px] text-slate-500 italic">
        Source: {bath.source}
      </p>
    </div>
  );
}

function Field({ label, unit, value, onChange, min, max, step }: {
  label: string; unit: string; value: number; onChange: (v: number) => void;
  min: number; max: number; step: number;
}) {
  return (
    <div>
      <label className="text-slate-400 block mb-1">{label}: <strong className="text-slate-100">{Number.isInteger(step) ? value : value.toFixed(2)} {unit}</strong></label>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full accent-sky-500" />
    </div>
  );
}
